/**
 * Stage selector strip — public-viewer MVP.
 *
 * Horizontal chip row: All + one chip per stage (SS1/H1 label + start
 * time). Selecting a stage scopes the viewer: car taps mount that
 * car's session ATTRIBUTED to the stage (M3 stage_index, via the
 * public `/events/<id>/stages/<i>/sessions` endpoint) instead of the
 * default live/latest recording, and the Cars list badges who has
 * footage there. "All" restores the untouched live-first behavior.
 *
 * Rendered only on multi-stage events (≥2 stage indices). Three skins:
 * `glass` floats over a map as its own dark pill, `panel` sits in
 * normal flow (mobile, above the context panel), `header` is BARE —
 * chips directly on the EventHeader background, no container chrome,
 * so the header keeps its natural height.
 */

import React, { useEffect, useRef, useState } from 'react';

import { cn } from '@/lib/utils';

export interface StageOption {
  index: number;
  /** SS1 / H2 — from stageModel.stageLabel. */
  label: string;
  /** Stage display name ('' = unnamed). */
  name: string;
  /** 'HH:MM' event-local start, or null. */
  startTime: string | null;
}

export const StageStrip: React.FC<{
  options: StageOption[];
  /** Selected stage index; null = All. */
  selected: number | null;
  onChange: (index: number | null) => void;
  skin: 'glass' | 'panel' | 'header';
  className?: string;
}> = ({ options, selected, onChange, skin, className }) => {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const selectedRef = useRef<HTMLButtonElement>(null);
  // Edge fades: the scrollbar is hidden, so without them a chip row
  // that overflows (SS5+ on phones) is invisible — fans never learn
  // the later stages exist. Fade whichever edge has more content.
  const [fade, setFade] = useState({ left: false, right: false });

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const update = () => {
      setFade({
        left: el.scrollLeft > 4,
        right: el.scrollLeft + el.clientWidth < el.scrollWidth - 4,
      });
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', update);
      ro.disconnect();
    };
  }, [options.length]);

  // Keep the active chip visible — a stage picked from a shared
  // ?stage= URL may start scrolled out of view.
  useEffect(() => {
    selectedRef.current?.scrollIntoView({ inline: 'nearest', block: 'nearest' });
  }, [selected]);

  const maskStops = [
    fade.left ? 'transparent 0, black 28px' : 'black 0',
    fade.right ? 'black calc(100% - 28px), transparent 100%' : 'black 100%',
  ].join(', ');

  return (
    <div
      className={cn(
        skin === 'glass' &&
          'max-w-[70vw] rounded-full border border-white/10 bg-black/70 p-1.5 backdrop-blur',
        skin === 'panel' && 'border-b border-border-subtle bg-bg-base px-2 py-1.5',
        // header: bare chip row — the EventHeader owns the chrome.
        className,
      )}
    >
      <div
        ref={scrollerRef}
        role="tablist"
        aria-label="Stage"
        className={cn(
          'flex items-center gap-1 overflow-x-auto',
          // Hide the scrollbar; the row pans by touch/wheel.
          '[-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
        )}
        style={{
          WebkitMaskImage: `linear-gradient(to right, ${maskStops})`,
          maskImage: `linear-gradient(to right, ${maskStops})`,
        }}
      >
        <StageChip
          active={selected == null}
          skin={skin}
          onClick={() => onChange(null)}
          chipRef={selected == null ? selectedRef : undefined}
        >
          All
        </StageChip>
        {options.map((o) => (
          <StageChip
            key={o.index}
            active={selected === o.index}
            skin={skin}
            onClick={() => onChange(o.index)}
            title={o.name || o.label}
            chipRef={selected === o.index ? selectedRef : undefined}
          >
            {o.label}
            {o.startTime && (
              <span className={cn('ml-1 font-mono font-normal', !((selected === o.index)) && 'opacity-60')}>
                {o.startTime}
              </span>
            )}
          </StageChip>
        ))}
      </div>
    </div>
  );
};

const StageChip: React.FC<{
  active: boolean;
  skin: 'glass' | 'panel' | 'header';
  onClick: () => void;
  title?: string;
  /** Set on the ACTIVE chip so the strip can scroll it into view. */
  chipRef?: React.Ref<HTMLButtonElement>;
  children: React.ReactNode;
}> = ({ active, skin, onClick, title, chipRef, children }) => (
  <button
    ref={chipRef}
    type="button"
    role="tab"
    aria-selected={active}
    title={title}
    onClick={onClick}
    className={cn(
      'shrink-0 whitespace-nowrap rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-wide',
      active
        ? 'bg-accent text-fg-inverse'
        : skin === 'panel'
          ? 'text-fg-muted hover:text-fg'
          : 'text-fg-secondary hover:text-fg',
    )}
  >
    {children}
  </button>
);

export default StageStrip;
